import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import axios from 'axios';

const formatCodice = (codice) => String(codice).replace(/(.{4})(?=.)/g, '$1-');

const formatDate = (value) =>
  new Date(value).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const formatTime = (value) =>
  value
    ? new Date(value).toLocaleTimeString('it-IT', { timeStyle: 'short' })
    : '';

function TagliandoCard({ tagliando, onOpen }) {
  const servito = tagliando.stato === 'servito';
  return (
    <Box
      as={servito ? 'div' : 'button'}
      onClick={servito ? undefined : () => onOpen(tagliando)}
      textAlign="center"
      p={3}
      bg="#36393f"
      borderRadius="12px"
      border="2px solid"
      borderColor={servito ? 'gray.600' : '#ffcc00'}
      opacity={servito ? 0.55 : 1}
      w="100%"
    >
      <Text color="white" fontWeight="700" fontSize="lg" noOfLines={2}>
        {tagliando.prodottoNome}
      </Text>
      {servito ? (
        <Badge mt={2} bg="gray.600" color="white" borderRadius="full" px={3}>
          Servito {formatTime(tagliando.servitoAt)}
        </Badge>
      ) : (
        <>
          <Box
            bg="white"
            p={2}
            borderRadius="8px"
            mt={2}
            mx="auto"
            maxW="200px"
          >
            <Image
              src={tagliando.qr}
              alt={`QR ${tagliando.prodottoNome}`}
              w="100%"
            />
          </Box>
          <Text color="gray.300" fontFamily="mono" fontSize="sm" mt={2}>
            {formatCodice(tagliando.codice)}
          </Text>
        </>
      )}
    </Box>
  );
}

/** Tagliandi F&B del socio: un QR per consumazione da mostrare al bar. */
const TagliandiFb = ({ onLoaded }) => {
  const [ricevute, setRicevute] = useState([]);
  const [aperto, setAperto] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/fb/mie-ricevute', {
        withCredentials: true,
      });
      setRicevute(data.data || []);
      onLoaded?.((data.data || []).length);
    } catch (err) {
      console.error('Errore caricamento tagliandi:', err);
      onLoaded?.(0);
    }
  }, [onLoaded]);

  useEffect(() => {
    load();
  }, [load]);

  if (ricevute.length === 0) return null;

  return (
    <Box mb={8}>
      <Flex justify="space-between" align="center" mb={2} gap={3}>
        <Heading size="md" color="white" fontWeight="700">
          🍹 I tuoi tagliandi
        </Heading>
        <Button
          size="sm"
          variant="outline"
          color="gray.300"
          borderColor="gray.600"
          _hover={{ bg: '#36393f' }}
          onClick={load}
        >
          Aggiorna
        </Button>
      </Flex>
      <Text color="gray.300" fontSize="sm" mb={4}>
        Mostra al bar un QR per ogni consumazione: tocca un tagliando per
        ingrandirlo. Ogni QR vale una volta sola.
      </Text>
      <VStack spacing={6} align="stretch">
        {ricevute.map((r) => {
          const daServire = r.tagliandi.filter(
            (t) => t.stato === 'da_servire'
          ).length;
          return (
            <Box key={r.documentId}>
              <Flex
                justify="space-between"
                align="baseline"
                mb={3}
                gap={2}
                wrap="wrap"
              >
                <Text color="white" fontWeight="600">
                  {r.evento?.Nome || 'Consumazioni'} · {formatDate(r.createdAt)}
                </Text>
                <Text color="gray.400" fontSize="sm">
                  Ricevuta n. {r.numero} · €{Number(r.totale).toFixed(2)} ·{' '}
                  {daServire} da usare
                </Text>
              </Flex>
              <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={3}>
                {r.tagliandi.map((t) => (
                  <TagliandoCard
                    key={t.codice}
                    tagliando={t}
                    onOpen={setAperto}
                  />
                ))}
              </SimpleGrid>
            </Box>
          );
        })}
      </VStack>

      <Modal
        isOpen={Boolean(aperto)}
        onClose={() => {
          setAperto(null);
          load(); // il bar potrebbe averlo appena servito
        }}
        size="full"
      >
        <ModalOverlay />
        <ModalContent bg="white">
          <ModalCloseButton size="lg" />
          <ModalBody
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={4}
          >
            {aperto && (
              <>
                <Text fontSize="3xl" fontWeight="800" color="#3b235a">
                  {aperto.prodottoNome}
                </Text>
                <Image
                  src={aperto.qr}
                  alt={`QR ${aperto.prodottoNome}`}
                  w="min(85vw, 420px)"
                />
                <Text
                  fontFamily="mono"
                  fontSize="2xl"
                  fontWeight="700"
                  color="black"
                >
                  {formatCodice(aperto.codice)}
                </Text>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TagliandiFb;
